import { Injectable, Optional } from '@angular/core';
import { WorkflowItemDirective } from '@workflow/directives/workflow-item/workflow-item.directive';
import { ProjectService } from '@app/services/project/project.service';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, switchMapTo } from 'rxjs/operators';
import { QueryRestService } from '@app/services/rest/query-rest.service';
import { firstValueFrom } from '@app/util/rxjs-7-compat';
import { QueryFilterDto, QueryResponseDto, WorkflowQueryDto } from '@interfaces/queries';
import { PermissionService } from '@app/services/permission/permission.service';
import { UnsubscribeOnDestroy } from '@app/util/lifecycle-hooks/lifecycle-hooks';

interface Context {
	projectId: string;
	participantId: string;
	itemId: string;
}

@Injectable()
export class WorkflowItemQueriesService {
	@UnsubscribeOnDestroy
	private readonly subscription = new Subscription();
	
	private readonly context$: Observable<Context | null>;
	private readonly changed$ = new BehaviorSubject<void>(undefined);
	
	public readonly active$: Observable<WorkflowQueryDto[]>;
	
	constructor(
		projectService: ProjectService,
		activeParticipantService: ActiveParticipantService,
		@Optional() workflowItemService: WorkflowItemDirective,
		private readonly permissionService: PermissionService,
		private readonly queryRestService: QueryRestService,
	) {
		this.context$ = combineLatest([
			projectService.activeProject$.pipe(map(project => project?.project_id)),
			activeParticipantService.participant$.pipe(map(participant => participant?.id)),
			workflowItemService?.workflowItem$.pipe(map(item => item?.id)) ?? of(undefined),
		]).pipe(
			filter(([,,itemId]) => itemId),
			map(([projectId, participantId, itemId]) => projectId && participantId ? { projectId, participantId, itemId } : null),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
		
		const active$ = new BehaviorSubject<WorkflowQueryDto[]>([]);
		
		this.subscription.add(
			this.query$().subscribe(active => active$.next(active))
		);
		
		this.active$ = active$.asObservable();
	}
	
	public query$(filter?: Omit<QueryFilterDto, 'participantids' | 'itemids'>): Observable<WorkflowQueryDto[]>{
		return this.changed$.pipe(
			switchMapTo(combineLatest([this.context$, this.permissionService.hasPermission$('query:read')])),
			switchMap(([context, hasPermission]) => {
				if (hasPermission && context) {
					const { projectId, participantId, itemId } = context;
					
					return this.queryRestService.list(projectId, {
						participantids: [participantId],
						itemids: [itemId],
						...filter,
					});
				} else {
					return of([]);
				}
			}),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}
	
	public async create(message: string, workflowKey?: string): Promise<WorkflowQueryDto> {
		const { projectId, participantId, itemId } = await firstValueFrom(this.context$);
		
		const result = await firstValueFrom(this.queryRestService.create(
			projectId,
			{
				participantid: participantId,
				itemid: itemId,
				workflowKey,
				openingMessage: message,
			}));
		
		this.changed$.next();
		
		return result;
	}
	
	public async respond(queryId: string, change: QueryResponseDto): Promise<void> {
		const { projectId } = await firstValueFrom(this.context$);
		
		await firstValueFrom(this.queryRestService.update(projectId, queryId, change));
		
		this.changed$.next();
	}
	
	public async delete(queryId: string, interactionIndex: number, message: string): Promise<void> {
		const { projectId } = await firstValueFrom(this.context$);
		
		await firstValueFrom(this.queryRestService.deleteComment(projectId, queryId, interactionIndex, message));
		
		this.changed$.next();
	}
}
